<script setup lang="ts">
  import { Button } from '@travelata-libs/ui-kit';
  import { computed, ref } from 'vue';
  import { type SearchCriteriaHistory } from '@/entities/searchCriteria';
  import { LeadBanner } from '@/features/leadBanner';
  import { type SwitchCity } from '@/features/switchCity';
  import type { AppDownloadStore } from '@/shared/model';
  import { EmiratesBanner } from '@/widgets/emiratesBanner';
  import {
    Footer,
    FooterArticlePage,
    resolveFooterArticlePage,
    type BuyTourSeo,
    type FooterArticleView,
    type FooterLinkAction,
  } from '@/widgets/footer';
  import { SearchFormMobileFlow } from '@/widgets/searchForm';
  import { Travelata15 } from '@/widgets/travelata15';
  import type { DestinationPrices, SearchCriteria } from 'src';
  import IndexBackground from '../IndexBackground.vue';
  import IndexMobileHeader from './IndexMobileHeader.vue';
  import IndexMobileTabs from './IndexMobileTabs.vue';
  import { type Tab, type TabType } from '../../model/types';

  interface IndexPageMobileProps {
    activeTab: TabType;
    tabs: Tab[];
    modelValue: SearchCriteria;
    isVisibleDeparture: boolean;
    isVisibleRoomButton: boolean;
    prices?: DestinationPrices;
    formUrl?: string;
    searchUrl?: string;
    resortsUrl?: string;
    accountUrl?: string;
    historySearch: SearchCriteriaHistory[];
    leadTitle: string;
    leadSubtitle: string;
    modalTitle?: string;
    modalSubtitle?: string;
    cities?: SwitchCity[];
    currentCity?: SwitchCity | null;
    buyTourSeo?: BuyTourSeo;
    visibleStorages?: AppDownloadStore[];
    searchBtnText?: string;
    dateFieldLabel?: string;
    nightsFieldLabel?: string;
  }

  const props = withDefaults(defineProps<IndexPageMobileProps>(), {
    prices: undefined,
    modalSubtitle: '',
    formUrl: '',
    searchUrl: '',
    resortsUrl: '',
    accountUrl: '',
    modalTitle: '',
    cities: () => [],
    currentCity: null,
    buyTourSeo: undefined,
    visibleStorages: undefined,
    searchBtnText: undefined,
    dateFieldLabel: undefined,
    nightsFieldLabel: undefined,
  });

  interface IndexPageMobileEmits {
    (e: 'update:modelValue', value: SearchCriteria): void;
    (e: 'select', type: TabType): void;
    (e: 'search', value: SearchCriteria): void;
    (e: 'back'): void;
    (e: 'chat'): void;
    (e: 'change:city', city: SwitchCity): void;
    (e: 'download', store: AppDownloadStore): void;
  }

  defineEmits<IndexPageMobileEmits>();

  const flowRef = ref<{ openHistory?: () => void; openMenu?: () => void } | null>(null);

  const onOpenHistory = (): void => {
    flowRef.value?.openHistory?.();
  };

  const onOpenMenu = (): void => {
    flowRef.value?.openMenu?.();
  };

  const activeFooterPage = ref<FooterLinkAction | null>(null);
  const flipping = ref(false);
  const onFooterNavigate = (action: FooterLinkAction): void => {
    flipping.value = true;
    activeFooterPage.value = action;
  };
  const closeFooterPage = (): void => {
    activeFooterPage.value = null;
  };
  const onFlipAfterLeave = (): void => {
    flipping.value = false;
  };
  const activeFooterPageView = computed<FooterArticleView | null>(() =>
    activeFooterPage.value ? resolveFooterArticlePage(activeFooterPage.value, props.buyTourSeo) : null,
  );
</script>

<template>
  <IndexBackground
    :tab="activeTab"
    mobile
    class="index-page-mobile"
    data-testid="index-page-mobile"
  >
    <div
      class="index-page-mobile__viewport"
      :class="{ 'index-page-mobile__viewport_flipping': flipping }"
    >
      <div
        class="index-page-mobile__flow-panel"
        :class="{ 'index-page-mobile__flow-panel_out': Boolean(activeFooterPage) }"
      >
        <SearchFormMobileFlow
          ref="flowRef"
          class="index-page-mobile__flow"
          :model-value="modelValue"
          :form-url="formUrl"
          :search-url="searchUrl"
          :resorts-url="resortsUrl"
          :account-url="accountUrl"
          :is-visible-departure="isVisibleDeparture"
          :is-visible-room-button="isVisibleRoomButton"
          :prices="prices"
          :history-search="historySearch"
          :switch-cities="cities"
          :switch-current-city="currentCity"
          :visible-storages="visibleStorages"
          :search-btn-text="searchBtnText"
          :date-field-label="dateFieldLabel"
          :nights-field-label="nightsFieldLabel"
          @update:modelValue="$emit('update:modelValue', $event)"
          @search="$emit('search', $event)"
          @change:city="$emit('change:city', $event)"
          @chat="$emit('chat')"
          @download="$emit('download', $event)"
        >
          <template #header>
            <IndexMobileHeader
              @menu="onOpenMenu"
              @chat="$emit('chat')"
            />
            <IndexMobileTabs
              :tabs="tabs"
              :active="activeTab"
              @select="$emit('select', $event)"
            />
          </template>

          <template #button-prepend>
            <Button
              v-if="historySearch.length"
              type="button"
              class="index-page-mobile__history"
              appearance="tertiary"
              icon-left="clock"
              data-testid="index-mobile-history-btn"
              @click="onOpenHistory"
            />
          </template>

          <template #footer>
            <div class="index-page-mobile__banners">
              <Travelata15></Travelata15>

              <LeadBanner
                :title="`${leadTitle} ${leadSubtitle}`"
                :subtitle="modalSubtitle"
                :modal-title="modalTitle"
                :mobile="true"
                class="index-page-mobile__banner"
              />

              <EmiratesBanner class="index-page-mobile__emirates" />
            </div>
          </template>

          <template #pageFooter>
            <Footer
              class="index-page-mobile__footer"
              :buy-tour-seo="buyTourSeo"
              :visible-storages="visibleStorages"
              @download="$emit('download', $event)"
              @navigate="onFooterNavigate"
            />
          </template>
        </SearchFormMobileFlow>
      </div>

      <transition
        name="index-page-mobile-page"
        @after-leave="onFlipAfterLeave"
      >
        <FooterArticlePage
          v-if="activeFooterPageView"
          :header-title="activeFooterPageView.headerTitle"
          :main-title="activeFooterPageView.mainTitle"
          :content="activeFooterPageView.content"
          @close="closeFooterPage"
          @chat="$emit('chat')"
        />
      </transition>
    </div>
  </IndexBackground>
</template>

<style lang="scss" scoped>
  .index-page-mobile {
    display: flex;
    flex-direction: column;
    min-height: 100dvh;
    width: 100%;

    &__viewport {
      display: flex;
      flex: 1 1 auto;
      flex-direction: column;
      width: 100%;

      &_flipping {
        overflow-x: clip;
      }
    }

    &__history {
      background: transparent;
      height: initial;
    }

    &__flow-panel {
      display: flex;
      flex: 1 1 auto;
      flex-direction: column;
      transition: transform 0.3s ease;

      &_out {
        transform: translateX(-100%);
      }
    }

    &__flow {
      flex: 1 1 auto;
    }

    &__banners {
      display: flex;
      flex-direction: column;
      gap: var(--spacing-xs);
      margin-top: var(--spacing-l);
    }

    &__banner {
      background: linear-gradient(
        233deg,
        var(--color-cyan-40) 0%,
        var(--color-brand-secondary) 73%,
        var(--color-sky-50) 100%
      );
      border: none;
      border-radius: var(--rounding-xl);
      box-shadow: 1px 1px 8px 0 rgb(0 0 0 / 0.15);
      box-sizing: border-box;
      color: var(--color-text-inverse);
      padding: var(--spacing-m);
      width: 100%;

      ::v-deep .tl-button {
        background-color: transparent;
        border: 1px solid var(--color-text-inverse);
      }
    }
  }

  .index-page-mobile-page-enter-active,
  .index-page-mobile-page-leave-active {
    transition: transform 0.3s ease;
  }

  .index-page-mobile-page-enter,
  .index-page-mobile-page-leave-to {
    transform: translateX(100%);
  }
</style>
